import type dayjs from 'dayjs';
import Decimal from 'decimal.js';
import type { TrayChangeInfo } from './OrderInputsChangeTable';
import type { TraysFromForms } from './getTrayCounts';

interface PlantingData {
  readonly id: string;
  readonly planting_status: string;
  readonly trays_rounded: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
    readonly yield_unit: string;
    readonly total_grow_time: number;
    readonly harvest_buffer_time: number;
  } | null;
}

interface Inputs {
  readonly traysFromForms: TraysFromForms;
  readonly orderPlantingData: readonly PlantingData[];
  readonly surplusPlantings: {
    readonly id: string;
    readonly trays_rounded: string;
    readonly planting_time: number;
    readonly crop?: {
      readonly id: string;
      readonly max_hold_time: number;
    } | null;
  }[];
  readonly orderFulfillmentTime: dayjs.Dayjs | null;
  readonly todayStartDate: dayjs.Dayjs;
}

export function getOrderChanges({
  traysFromForms,
  orderPlantingData,
  surplusPlantings,
  orderFulfillmentTime,
  todayStartDate,
}: Inputs): TrayChangeInfo[] {
  const traysFromPlantings = orderPlantingData.reduce(
    (acc, planting) => {
      if (planting.crop == null) {
        return acc;
      }
      const maybeExistingCrop = acc.get(planting.crop.id);
      if (maybeExistingCrop === undefined) {
        acc.set(planting.crop.id, { trays: new Decimal(planting.trays_rounded), plantingData: planting });
      } else {
        acc.set(planting.crop.id, {
          trays: maybeExistingCrop.trays.add(planting.trays_rounded),
          plantingData: planting,
        });
      }

      return acc;
    },
    new Map<
      string,
      {
        readonly trays: Decimal;
        readonly plantingData: PlantingData;
      }
    >(),
  );
  const cropIdToPlanting = new Map<string, PlantingData>();
  for (const planting of orderPlantingData) {
    if (planting.crop == null) {
      continue;
    }
    cropIdToPlanting.set(planting.crop.id, planting);
  }

  const traysFromSurpluses =
    surplusPlantings.reduce<Map<string, Decimal>>((acc, planting) => {
      if (planting.crop == null) {
        return acc;
      }
      if (orderFulfillmentTime == null) {
        return acc;
      }
      // Check that the plantings can be used for this order
      // Harvest time must be before or equal to order time
      // And harvest time + max hold time (shelf life) must be after or equal to order time
      const harvestTime = planting.planting_time;
      const maxHoldTime = planting.crop.max_hold_time ?? 0;
      if (harvestTime > orderFulfillmentTime.unix() || harvestTime + maxHoldTime < orderFulfillmentTime.unix()) {
        return acc;
      }

      const cropId = planting.crop.id;
      const maybeExisting = acc.get(cropId);
      if (maybeExisting === undefined) {
        acc.set(cropId, new Decimal(planting.trays_rounded));
      } else {
        acc.set(cropId, maybeExisting.add(planting.trays_rounded));
      }

      return acc;
    }, new Map<string, Decimal>()) ?? new Map<string, Decimal>();

  const changes: TrayChangeInfo[] = [];

  // Check if any crops are deleted
  const existingCropIds = new Set(traysFromPlantings.keys());
  const currentCropIds = new Set(traysFromForms.keys());
  const deletedCropIds = new Set([...existingCropIds].filter((id) => !currentCropIds.has(id)));

  // if today start + grow time is later than the order fulfillment time day

  for (const cropId of deletedCropIds) {
    const crop = traysFromPlantings.get(cropId);
    const cropData = crop?.plantingData.crop;
    if (crop === undefined || cropData == null) {
      continue;
    }
    // orderFulfillmentTime will be null at the same time as the traysFromForms is empty, while data is loading
    if (orderFulfillmentTime == null) {
      continue;
    }
    const necessaryStartDay = orderFulfillmentTime.subtract(cropData.total_grow_time, 'seconds');
    const cropCanBeStartedInTime =
      necessaryStartDay.isSame(todayStartDate, 'day') || necessaryStartDay.isAfter(todayStartDate, 'day');
    changes.push({
      cropId,
      cropName: cropData.crop_name,
      totalGrowTime: cropData.total_grow_time,
      harvestBufferTime: cropData.harvest_buffer_time,
      didChange: true,
      cropCanBeStartedInTime,
      isAdded: false,
      isRemoved: true,
      trayDiff: crop.trays.neg(),
      existingTrays: crop.trays,
      newTrays: new Decimal(0),
      plantingData: crop.plantingData,
      surplusTraysAvailable: traysFromSurpluses.get(cropId) ?? new Decimal(0),
    });
  }

  for (const [cropId, traysFromForm] of traysFromForms) {
    const maybeExistingCrop = traysFromPlantings.get(cropId);
    const existingTrays = maybeExistingCrop?.trays ?? new Decimal(0);
    // Round new trays first, just like we would on the backend
    const newTrays = new Decimal(traysFromForm.trayCount.toFixed(2));
    const trayDiff = newTrays.sub(existingTrays);
    // orderFulfillmentTime will be null at the same time as the traysFromForms is empty, while data is loading
    // if (orderFulfillmentTime == null) {
    //   continue;
    // }

    // TODO: describe why this is the way is it is

    const necessaryStartDay = orderFulfillmentTime?.subtract(traysFromForm.cropData.total_grow_time, 'seconds');
    const cropCanBeStartedInTime = necessaryStartDay
      ? necessaryStartDay.isSame(todayStartDate, 'day') || necessaryStartDay.isAfter(todayStartDate, 'day')
      : true;
    if (trayDiff.isZero()) {
      changes.push({
        cropId,
        cropName: traysFromForm.cropData.crop_name,
        totalGrowTime: traysFromForm.cropData.total_grow_time,
        harvestBufferTime: traysFromForm.cropData.harvest_buffer_time,
        cropCanBeStartedInTime,
        didChange: false,
        isAdded: false,
        isRemoved: false,
        trayDiff: new Decimal(0),
        existingTrays,
        newTrays,
        plantingData: cropIdToPlanting.get(cropId),
        surplusTraysAvailable: traysFromSurpluses.get(cropId) ?? new Decimal(0),
      });
      continue;
    }
    changes.push({
      cropId,
      cropName: traysFromForm.cropData.crop_name,
      totalGrowTime: traysFromForm.cropData.total_grow_time,
      harvestBufferTime: traysFromForm.cropData.harvest_buffer_time,
      cropCanBeStartedInTime,
      didChange: true,
      isAdded: maybeExistingCrop === undefined,
      isRemoved: false,
      trayDiff,
      existingTrays,
      newTrays,
      plantingData: cropIdToPlanting.get(cropId),
      surplusTraysAvailable: traysFromSurpluses.get(cropId) ?? new Decimal(0),
    });
  }

  return changes;
}
